import { generateSecretKey, getPublicKey, finalizeEvent, nip19, nip44 } from 'nostr-tools';
import type { NostrEvent } from '@nostrify/nostrify';
import { ADMIN_PUBKEY } from './admin';
import {
  BUSINESS_KIND,
  BUSINESS_D_TAG,
  BUSINESS_TAG,
  BUSINESS_CAT_PREFIX,
  BUSINESS_TYPE_PREFIX,
} from './business';
import { encodeGeohash } from './geohash';

/**
 * "List your business directly on BCH Local" flow.
 *
 * A business owner fills out a form and pays a fee. The app generates a
 * dedicated Nostr keypair for the business and publishes a kind-0 profile plus
 * a kind-30499 listing signed by that key — identical in format to BCHNostr
 * listings, so the business appears on BCH Local *and* on BCHNostr without the
 * owner ever creating a BCHNostr account.
 *
 * The generated key is "app-managed": it's shown to the owner once (as a backup)
 * but edits/removals are normally handled by the BCH Local admin.
 */

/** Categories offered in the direct-listing form (BCHNostr category IDs). */
export const DIRECT_LISTING_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'food', label: 'Food & Drink' },
  { id: 'retail', label: 'Retail' },
  { id: 'services', label: 'Services' },
  { id: 'online', label: 'Online' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'technology', label: 'Technology' },
  { id: 'other', label: 'Other' },
];

/** Data collected from the direct-listing form. */
export interface DirectListingDraft {
  name: string;
  category: string;
  tagline: string;
  bch: string;
  website: string;
  onlineOnly: boolean;
  /** Free-text location (e.g. "Austin, TX"). */
  location: string;
  /** Optional precise coordinates for map placement. */
  lat?: number;
  lng?: number;
}

/** A freshly generated, app-managed business identity. */
export interface GeneratedIdentity {
  /** Hex secret key. */
  secretKeyHex: string;
  /** Bech32 nsec (shown once to the owner as a backup). */
  nsec: string;
  /** Hex public key. */
  pubkey: string;
  /** Bech32 npub. */
  npub: string;
  /** Raw secret key bytes for signing. */
  secretKey: Uint8Array;
}

/** Generate a new managed business identity. */
export function generateBusinessIdentity(): GeneratedIdentity {
  const secretKey = generateSecretKey();
  const pubkey = getPublicKey(secretKey);
  const secretKeyHex = bytesToHex(secretKey);
  return {
    secretKey,
    secretKeyHex,
    nsec: nip19.nsecEncode(secretKey),
    pubkey,
    npub: nip19.npubEncode(pubkey),
  };
}

function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/** Build the kind-0 profile content for a directly-listed business. */
export function buildProfileContent(draft: DirectListingDraft): string {
  return JSON.stringify({
    name: draft.name.trim(),
    about: draft.tagline.trim() || 'A Bitcoin Cash business on BCH Local.',
    website: draft.website.trim() || undefined,
  });
}

/**
 * Build the kind-30499 listing event template (unsigned). Matches BCHNostr's
 * `buildBusinessEvent` tag/content structure so the listing is cross-compatible.
 */
export function buildListingTemplate(draft: DirectListingDraft): {
  kind: number;
  tags: string[][];
  content: string;
  created_at: number;
} {
  const now = Math.floor(Date.now() / 1000);
  const proType = 'business';

  const tags: string[][] = [
    ['d', BUSINESS_D_TAG],
    ['t', BUSINESS_TAG],
    ['t', `${BUSINESS_TYPE_PREFIX}${proType}`],
  ];

  if (draft.category) tags.push(['t', `${BUSINESS_CAT_PREFIX}${draft.category}`]);
  if (draft.name.trim()) tags.push(['name', draft.name.trim()]);

  const bch = draft.bch.trim().replace(/^bitcoincash:/i, '');
  if (bch) tags.push(['bch', bch]);

  if (!draft.onlineOnly) {
    if (typeof draft.lat === 'number' && typeof draft.lng === 'number') {
      tags.push(['g', encodeGeohash(draft.lat, draft.lng, 6)]);
    }
    if (draft.location.trim()) tags.push(['location', draft.location.trim()]);
  }

  // Mark provenance so we can distinguish app-originated listings.
  tags.push(['source', 'bch-local']);
  tags.push(['alt', 'A Bitcoin Cash business listing on BCH Local']);

  const content = JSON.stringify({
    proType,
    category: draft.category || undefined,
    tagline: draft.tagline.trim() || undefined,
    website: draft.website.trim() || undefined,
    onlineOnly: draft.onlineOnly || undefined,
  });

  return { kind: BUSINESS_KIND, tags, content, created_at: now };
}

/** Sign an event template with a generated secret key. */
export function signWithKey(
  template: { kind: number; tags: string[][]; content: string; created_at: number },
  secretKey: Uint8Array,
): NostrEvent {
  return finalizeEvent(template, secretKey) as unknown as NostrEvent;
}

/** Kind used to store an encrypted backup of a listing's secret key. */
export const KEY_BACKUP_KIND = 30078;
/** `d` tag prefix for a per-listing encrypted key backup. */
export const KEY_BACKUP_D_PREFIX = 'bch-local-key-backup/';
/** `t` marker for key-backup events. */
export const KEY_BACKUP_TAG = 'bch-local-key-backup';

/**
 * Build an event that stores the listing's secret key, encrypted so ONLY the
 * admin can read it.
 *
 * The nsec is NIP-44 encrypted using the conversation key between the listing
 * key and the admin pubkey (`conv(listingPriv, adminPub)`). Because NIP-44
 * conversation keys are symmetric, the admin later decrypts with
 * `conv(adminPriv, listingPub)` via their signer — no plaintext key ever
 * touches a relay.
 *
 * The event is authored (signed) by the listing key itself, and tagged with
 * the admin pubkey so the admin can find backups addressed to them.
 */
export function buildEncryptedKeyBackup(identity: GeneratedIdentity): NostrEvent {
  const convKey = nip44.v2.utils.getConversationKey(identity.secretKey, ADMIN_PUBKEY);
  const ciphertext = nip44.v2.encrypt(identity.nsec, convKey);

  const template = {
    kind: KEY_BACKUP_KIND,
    tags: [
      ['d', `${KEY_BACKUP_D_PREFIX}${identity.pubkey}`],
      ['t', KEY_BACKUP_TAG],
      // Address the backup to the admin so it can be queried with #p.
      ['p', ADMIN_PUBKEY],
      ['alt', 'Encrypted recovery key for a BCH Local direct listing'],
    ],
    content: ciphertext,
    created_at: Math.floor(Date.now() / 1000),
  };

  return finalizeEvent(template, identity.secretKey) as unknown as NostrEvent;
}
